<template>
  <div class="debug-notes">
    <button
      type="button"
      class="debug-notes-trigger"
      aria-label="Abrir bloc de pruebas (errores y notas)"
      v-tooltip="{ value: 'Bloc de pruebas: errores, API y contexto de vista', position: 'bottom' }"
      @click="open = true"
    >
      <i class="pi pi-clipboard" aria-hidden="true" />
      <span v-if="errorBadgeCount > 0" class="debug-notes-badge" aria-live="polite">
        {{ errorBadgeCount > 99 ? '99+' : errorBadgeCount }}
      </span>
    </button>

    <SidebarPrime
      v-model:visible="open"
      position="right"
      class="debug-notes-sidebar"
      :style="{ width: 'min(28rem, 94vw)' }"
    >
      <div class="debug-notes-head">
        <div>
          <h2 class="debug-notes-title">Bloc de pruebas</h2>
          <p class="debug-notes-sub">
            Se guardan errores de la app, fallos HTTP y notas. Úsalo al probar y copia el texto para compartirlo.
          </p>
        </div>
      </div>

      <div class="debug-notes-actions">
        <ButtonPrime
          label="Capturar vista"
          icon="pi pi-camera"
          size="small"
          outlined
          @click="onCaptureVista"
        />
        <ButtonPrime
          label="Copiar todo"
          icon="pi pi-copy"
          size="small"
          outlined
          :disabled="!entries.length"
          @click="onCopyAll"
        />
        <ButtonPrime
          label="Limpiar"
          icon="pi pi-trash"
          size="small"
          severity="danger"
          outlined
          :disabled="!entries.length"
          @click="onClear"
        />
      </div>

      <div class="debug-notes-manual">
        <label class="debug-notes-manual-label" for="debug-note-title">Nota manual</label>
        <InputText
          id="debug-note-title"
          v-model="manualTitle"
          class="w-full mb-2"
          placeholder="Título breve"
        />
        <TextareaPrime
          v-model="manualBody"
          class="w-full"
          rows="3"
          autoResize
          placeholder="Detalle, pasos para reproducir, etc."
        />
        <ButtonPrime
          label="Añadir nota"
          icon="pi pi-plus"
          size="small"
          class="mt-2"
          :disabled="!manualTitle.trim() && !manualBody.trim()"
          @click="onAddManual"
        />
      </div>

      <p v-if="copyFeedback" class="debug-notes-feedback">{{ copyFeedback }}</p>

      <div class="debug-notes-list-wrap">
        <p v-if="!entries.length" class="debug-notes-empty">Aún no hay entradas.</p>
        <ul v-else class="debug-notes-list" role="list">
          <li v-for="e in entries" :key="e.id" class="debug-notes-item" :data-type="e.type">
            <div class="debug-notes-item-head">
              <span class="debug-notes-tag">{{ labelTipo(e.type) }}</span>
              <time class="debug-notes-time" :datetime="e.at">{{ formatAt(e.at) }}</time>
              <button
                type="button"
                class="debug-notes-item-del"
                aria-label="Quitar entrada"
                @click="remove(e.id)"
              >
                <i class="pi pi-times" aria-hidden="true" />
              </button>
            </div>
            <p class="debug-notes-item-title">{{ e.title }}</p>
            <pre v-if="e.route" class="debug-notes-route">{{ routeLine(e.route) }}</pre>
            <pre v-if="e.body" class="debug-notes-pre">{{ e.body }}</pre>
            <pre v-if="e.stack" class="debug-notes-pre debug-notes-stack">{{ e.stack }}</pre>
            <p v-if="e.vueInfo" class="debug-notes-vueinfo">Vue: {{ e.vueInfo }}</p>
          </li>
        </ul>
      </div>
    </SidebarPrime>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { captureVistaContext, formatEntriesForExport, pushDebugEntry } from '@/utils/debugLogHub';

export default {
  name: 'DebugNotesCollector',
  data() {
    return {
      open: false,
      manualTitle: '',
      manualBody: '',
      copyFeedback: '',
    };
  },
  computed: {
    ...mapGetters('debugLog', ['entries', 'errorBadgeCount']),
  },
  methods: {
    ...mapMutations('debugLog', ['clear', 'remove']),
    labelTipo(t) {
      const m = {
        error: 'Error',
        api: 'API',
        warn: 'Aviso',
        vista: 'Vista',
        note: 'Nota',
      };
      return m[t] || t;
    },
    formatAt(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    },
    routeLine(r) {
      if (!r) return '';
      return `Ruta: ${r.fullPath || r.path || ''} (${r.name || '—'})`;
    },
    onCaptureVista() {
      captureVistaContext();
      this.copyFeedback = 'Contexto de vista guardado.';
      this.clearFeedbackSoon();
    },
    onAddManual() {
      const title = this.manualTitle.trim() || 'Nota manual';
      const body = this.manualBody.trim() || '(sin texto)';
      pushDebugEntry({ type: 'note', title, body });
      this.manualTitle = '';
      this.manualBody = '';
      this.copyFeedback = 'Nota añadida.';
      this.clearFeedbackSoon();
    },
    onClear() {
      if (!this.entries.length) return;
      if (window.confirm('¿Vaciar todas las entradas del bloc?')) {
        this.clear();
        this.copyFeedback = 'Bloc vaciado.';
        this.clearFeedbackSoon();
      }
    },
    async onCopyAll() {
      const text = formatEntriesForExport(this.entries);
      try {
        await navigator.clipboard.writeText(text);
        this.copyFeedback = 'Copiado al portapapeles.';
      } catch {
        this.copyFeedback = 'No se pudo copiar (permiso o navegador).';
      }
      this.clearFeedbackSoon();
    },
    clearFeedbackSoon() {
      window.clearTimeout(this._fbTimer);
      this._fbTimer = window.setTimeout(() => {
        this.copyFeedback = '';
      }, 2800);
    },
  },
};
</script>

<style scoped>
.debug-notes {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.debug-notes-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  margin: 0;
  padding: 0;
  border: 1px solid var(--admin-border, rgba(15, 23, 42, 0.1));
  border-radius: 50%;
  background: #fff;
  color: var(--admin-text, #334155);
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.debug-notes-trigger:hover {
  background: #f8fafc;
  border-color: rgba(37, 99, 235, 0.35);
  color: var(--primary-color, #2563eb);
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.12);
}

.debug-notes-trigger:focus-visible {
  outline: 2px solid var(--primary-color, #2563eb);
  outline-offset: 2px;
}

.debug-notes-trigger .pi {
  font-size: 1.15rem;
}

.debug-notes-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 1.15rem;
  height: 1.15rem;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  background: #dc2626;
  border-radius: 999px;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.debug-notes-sidebar :deep(.p-sidebar-content) {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  max-height: 100vh;
  overflow: hidden;
}

.debug-notes-head {
  padding: 1rem 1.1rem 0.75rem;
  border-bottom: 1px solid var(--admin-border, #e2e8f0);
}

.debug-notes-title {
  margin: 0 0 0.35rem;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--admin-text, #0f172a);
}

.debug-notes-sub {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--admin-text-muted, #64748b);
}

.debug-notes-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--admin-border, #e2e8f0);
}

.debug-notes-manual {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--admin-border, #e2e8f0);
  background: #f8fafc;
}

.debug-notes-manual-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--admin-text-muted, #64748b);
  margin-bottom: 0.35rem;
}

.debug-notes-feedback {
  margin: 0;
  padding: 0.4rem 1rem;
  font-size: 0.8rem;
  color: #059669;
  background: #ecfdf5;
}

.debug-notes-list-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem 0.65rem 1rem;
}

.debug-notes-empty {
  margin: 1.5rem 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: var(--admin-text-muted, #94a3b8);
}

.debug-notes-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.debug-notes-item {
  border-radius: 10px;
  border: 1px solid var(--admin-border, #e2e8f0);
  background: #fff;
  padding: 0.55rem 0.65rem 0.65rem;
  font-size: 0.8rem;
}

.debug-notes-item[data-type='error'],
.debug-notes-item[data-type='api'] {
  border-color: rgba(220, 38, 38, 0.25);
  background: #fffafa;
}

.debug-notes-item[data-type='warn'] {
  border-color: rgba(217, 119, 6, 0.25);
  background: #fffbeb;
}

.debug-notes-item[data-type='vista'] {
  border-color: rgba(37, 99, 235, 0.2);
  background: #f8fafc;
}

.debug-notes-item-head {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.35rem;
}

.debug-notes-tag {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  background: #e2e8f0;
  color: #475569;
}

.debug-notes-item[data-type='error'] .debug-notes-tag,
.debug-notes-item[data-type='api'] .debug-notes-tag {
  background: #fee2e2;
  color: #b91c1c;
}

.debug-notes-item[data-type='warn'] .debug-notes-tag {
  background: #fef3c7;
  color: #b45309;
}

.debug-notes-item[data-type='vista'] .debug-notes-tag {
  background: #dbeafe;
  color: #1d4ed8;
}

.debug-notes-time {
  flex: 1;
  font-size: 0.68rem;
  color: #94a3b8;
}

.debug-notes-item-del {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: 4px;
  line-height: 0;
}

.debug-notes-item-del:hover {
  color: #dc2626;
  background: #fef2f2;
}

.debug-notes-item-title {
  margin: 0 0 0.35rem;
  font-weight: 600;
  font-size: 0.85rem;
  color: #0f172a;
  line-height: 1.35;
}

.debug-notes-route {
  margin: 0 0 0.35rem;
  padding: 0.35rem 0.45rem;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 0.72rem;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: ui-monospace, monospace;
}

.debug-notes-pre {
  margin: 0;
  padding: 0.4rem 0.45rem;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 6px;
  font-size: 0.7rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 12rem;
  overflow: auto;
}

.debug-notes-stack {
  margin-top: 0.35rem;
  max-height: 8rem;
  font-size: 0.65rem;
  opacity: 0.95;
}

.debug-notes-vueinfo {
  margin: 0.35rem 0 0;
  font-size: 0.7rem;
  color: #64748b;
}
</style>
